<template>
  <div class="session-notes-container" v-bind="$attrs">
    <div class="action-button-wrapper border-t-[0.5px] border-neutral-30 py-24">
      <se-button type="tertiary" @click="addNoteActive = true" class="w-full" :disabled="addNoteActive || editingNote">
        <se-icon name="plus" class="ml-6"/>
        Add note
      </se-button>
    </div>
    <div ref="notesContent">
      <div class="mb-24" v-if="addNoteActive">
        <se-note-form
            label-text="New Note"
            :active-form="addNoteActive"
            :loading="noteFormLoading"
            v-model="newNote"
            @cancel="cancelNewNote"
            @add="addNoteClick"
        />
      </div>
      <ul v-if="notesList?.length" class="flex flex-col items-start justify-between">
        <li v-for="(note, index) in notesList" ref="notesRef" class="w-full py-12 border-t-[0.5px] border-t-neutral-30">
          <div class="note-wrapper">
            <div class="flex justify-between items-center">
              <p class="text-h-s text-neutral-80 font-semibold">
                {{ useDateFormatter().formatDate(note.created_at, 'MMM d, y, H:mm') }}</p>
              <div class="action-anchors flex gap-2">
                <div class="p-1 cursor-pointer" @click="editNoteClick(note, index)">
                  <se-icon name="square-pen" size="16"/>
                </div>
                <div class="p-1 cursor-pointer" @click="openConfirmDeleteNote(note)">
                  <se-icon name="trash-2" size="16"/>
                </div>
              </div>
            </div>
            <p class="text-b-s text-neutral-80">{{ note?.body }}</p>
          </div>
          <se-note-form
              class="note-edit-form-container w-full"
              label-text="Edit Note"
              v-model="note.body"
              :loading="noteFormLoading"
              @cancel="() => cancelEditingNote(index)"
              @add="() => saveEditedNote(note, index)"
          />
        </li>
      </ul>
      <ul v-else>
        <li class="is-text-muted">No notes yet...</li>
      </ul>
    </div>
  </div>
  <se-confirm-popup :visible="confirmationPopupVisible"
                    question="Are you sure you want to delete this note?"
                    confirm-btn-text="Delete"
                    icon="trash-2"
                    type="error"
                    :loading="noteFormLoading"
                    @close="closeConfirmDeleteNote"
                    @confirm="deleteNoteClick"
  >
    <p class="text-b-sm line-clamp-3 text-center">{{ deletingNote?.body }}</p>
  </se-confirm-popup>
</template>

<script setup lang="ts">
import {ref, watch, withDefaults} from 'vue';
import useDateFormatter from "@/src/composables/useDateFormatter";
import useNotes from "@/src/composables/useNotes";
import SeNoteForm from "./note-form.vue";
import SeConfirmPopup from "./confirmation-popup.vue";
import SeIcon from "@/src/shared/ui-kit/icon/Icon.vue";
import SeButton from "@/src/shared/ui-kit/button/Button.vue";
import {Note} from "@/src/interfaces/note";

const props = withDefaults(defineProps<{
  notes?: any[],
  opponentId: any,
}>(), {
  notes: <any>[],
});

const emits = defineEmits(['updated'])

const confirmationPopupVisible = ref(false);
const addNoteActive = ref(false);
const newNote = ref("");
const deletingNote = ref(null);
const editingNote = ref(false);
const notesRef = ref<null | HTMLElement[]>(null);

const notesList = ref(props.notes)

watch(() => props.notes, (newVal) => notesList.value = newVal)

const {addNote, editNote, deleteNote, noteFormLoading} = useNotes()

const editNoteClick = (note: any, index: number) => {
  notesRef.value && notesRef.value[index].classList.add('edit-mode')
  editingNote.value = true
}

const saveEditedNote = (note: any, index: number) => {
  editNote(note.body, note.id).then(() => {
    cancelEditingNote(index);
    emits('updated')
  })
}

const cancelEditingNote = (index: number) => {
  notesRef.value && notesRef.value[index].classList.remove('edit-mode')
  editingNote.value = false
}

const openConfirmDeleteNote = (note: any) => {
  confirmationPopupVisible.value = true
  document.getElementById("appContent")?.classList.add("confirmation-popup-visible")
  deletingNote.value = note
}

const closeConfirmDeleteNote = () => {
  confirmationPopupVisible.value = false
  document.getElementById("appContent")?.classList.remove("confirmation-popup-visible")
  deletingNote.value = null
}

const deleteNoteClick = () => {
  deleteNote(deletingNote.value?.id).then(() => {
    const index = notesList.value.findIndex((item: Note) => item.id === deletingNote.value?.id);
    if (index !== -1) {
      notesList.value.splice(index, 1);
    }
    
    closeConfirmDeleteNote()
    emits('updated')
  })
}

const cancelNewNote = () => {
  addNoteActive.value = false;
}

const addNoteClick = () => {
  addNote(newNote.value, props.opponentId).then((res: any) => {
    addNoteActive.value = false;
    newNote.value = "";
    notesList.value.unshift(res)
    emits('updated')
  })
}

</script>

<script lang="ts">
export default {
  name: 'SeSessionNotes',
};
</script>

<style lang="scss">

.session-notes-container {
  .note-edit-form-container {
    @apply hidden;
  }

  .edit-mode {
    .note-wrapper {
      @apply hidden;
    }

    .note-edit-form-container {
      @apply visible block;
    }
  }
}

#appContent.confirmation-popup-visible {
  header, .chat-app-form {
    z-index: 1 !important;
  }
}

</style>